import { css, cva } from '@styled-system/css';
import {
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
} from 'chart.js';
import {
  Clock,
  Coins,
  Euro,
  ExternalLink,
  ImageOff,
  Package,
  Percent,
  PiggyBank,
  Play,
  TrendingUp,
} from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { dsTokens } from '@/shared/theme/tokens';

import type { TrendNiche } from './TrendTable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

// ============================================================================
// Popup de détail d'une niche Trend — MÊME DA que l'onglet analyse : tuiles
// de stats clés (surface.subtle, icône + label, valeur mono), courbe chart.js
// INTERACTIVE identique au graphe d'activité (tooltip au survol, aura brand).
// Data du contrat 2026-09-17 : métriques, série 14 j, ventes ≤ 5
// (titre/prix nullables), top vendeurs. CTA -> lancer l'analyse.
// ============================================================================

const header = css({ display: 'flex', alignItems: 'center', gap: '4', minWidth: 0 });

const headerImg = css({
  width: '56px',
  height: '56px',
  borderRadius: 'lg',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const headerImgFallback = css({
  width: '56px',
  height: '56px',
  borderRadius: 'lg',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const sectionTitle = css({
  fontSize: 'caption',
  fontWeight: 'semibold',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
});

// ─── Tuiles de stats — copie de l'habillage AnalysisKeyStats ────────────────

const statsGrid = css({
  display: 'grid',
  gridTemplateColumns: { base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  gap: '3',
});

const tile = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  minWidth: 0,
  justifyContent: 'center',
});

const tileHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'caption',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const tileValue = css({
  fontFamily: 'mono',
  fontSize: '18px',
  lineHeight: '1.15',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.5px',
  whiteSpace: 'nowrap',
});

const growthChip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingX: '3',
    paddingY: '0.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontFamily: 'mono',
    fontSize: 'caption',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  variants: {
    tier: {
      down: { bg: 'error.soft', color: 'error.default', borderColor: 'error.border' },
      up: { bg: 'success.soft', color: 'success.default', borderColor: 'success.border' },
    },
  },
});

const saleRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  padding: '3',
  borderRadius: 'lg',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  textDecoration: 'none',
  transition: 'border-color 120ms ease',
  _hover: { borderColor: 'border.strong' },
});

const saleImg = css({
  width: '38px',
  height: '38px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

/** Courbe 14 jours INTERACTIVE — même config que le graphe d'activité d'une analyse. */
const DetailCurve: FC<{ series: TrendNiche['series']; salesLabel: string }> = ({ series, salesLabel }) => {
  const { i18n } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
  if (series.length < 2) return null;

  const brand = ds['brand.primary'];
  return (
    <div className={css({ width: '100%', position: 'relative' })} style={{ height: 160 }}>
      <Line
        data={{
          labels: series.map((p) => new Date(p.day).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })),
          datasets: [
            {
              label: salesLabel,
              data: series.map((p) => p.sales),
              borderColor: brand,
              // Aura marquée sous la courbe, comme demandé sur les sparklines
              backgroundColor: `${brand}4D`,
              fill: true,
              tension: 0.35,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: brand,
              pointBorderColor: ds['surface.default'],
              pointHoverBorderWidth: 2,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: ds['surface.elevated'],
              titleColor: ds['text.primary'],
              bodyColor: ds['text.secondary'],
              borderColor: ds['border.default'],
              borderWidth: 1,
              padding: 8,
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: ds['text.tertiary'],
                font: { size: 10 },
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 7,
              },
              border: { color: ds['border.default'] },
            },
            y: {
              beginAtZero: true,
              grid: { color: `${ds['border.subtle']}80` },
              ticks: { color: ds['text.tertiary'], font: { size: 11 }, precision: 0 },
              border: { display: false },
            },
          },
        }}
      />
    </div>
  );
};

interface TrendNicheDetailModalProps {
  niche: TrendNiche | null;
  /** Libellé de croissance selon la liste (hebdo vs tendance). */
  growthLabel: string;
  onClose: () => void;
  onOpenAnalysis: (niche: TrendNiche) => void;
}

export const TrendNicheDetailModal: FC<TrendNicheDetailModalProps> = ({
  niche,
  growthLabel,
  onClose,
  onOpenAnalysis,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';

  if (!niche) return null;

  const imageUrl = niche.seed?.imageUrl ?? niche.lastSales[0]?.imageUrl ?? null;
  const context = [niche.brand?.name, niche.catalog?.leafName ?? niche.catalog?.level1Name ?? niche.catalog?.rootName]
    .filter(Boolean)
    .join(' · ');

  const euro = (value: number, digits = 0) => `${value.toLocaleString(locale, { maximumFractionDigits: digits })} €`;
  const hoursLabel = (value: number) => {
    const day = t('ui.timeUnits.day');
    const hour = t('ui.timeUnits.hour');
    const days = Math.floor(value / 24);
    const rest = Math.round(value % 24);
    if (days === 0) return `${rest}${hour}`;
    return rest > 0 ? `${days}${day} ${rest}${hour}` : `${days}${day}`;
  };

  const growth = niche.metrics.growthPct;
  const stats: { key: string; icon: ReactNode; label: string; value: string }[] = [
    {
      key: 'revenue',
      icon: <PiggyBank size={16} strokeWidth={1.75} />,
      label: t('trend.caPerMonth'),
      value: niche.metrics.caPerMonth != null ? euro(niche.metrics.caPerMonth) : '—',
    },
    {
      key: 'soldPct',
      icon: <Percent size={16} strokeWidth={1.75} />,
      label: t('niches.colSoldPct'),
      value: niche.metrics.soldPct != null ? `${Math.round(niche.metrics.soldPct)}%` : '—',
    },
    {
      key: 'salesPerDay',
      icon: <TrendingUp size={16} strokeWidth={1.75} />,
      label: t('niches.colSoldPerDay'),
      value:
        niche.metrics.salesPerDay != null
          ? niche.metrics.salesPerDay.toLocaleString(locale, { maximumFractionDigits: 1 })
          : '—',
    },
    {
      key: 'avgPrice',
      icon: <Euro size={16} strokeWidth={1.75} />,
      label: t('niches.colAvgPrice'),
      value: niche.metrics.avgPrice != null ? euro(niche.metrics.avgPrice, 2) : '—',
    },
    {
      key: 'medianPrice',
      icon: <Coins size={16} strokeWidth={1.75} />,
      label: t('trend.medianPrice'),
      value: niche.metrics.medianPrice != null ? euro(niche.metrics.medianPrice, 2) : '—',
    },
    {
      key: 'timeToSell',
      icon: <Clock size={16} strokeWidth={1.75} />,
      label: t('niches.colTimeToSell'),
      value: niche.metrics.timeToSellHours != null ? hoursLabel(niche.metrics.timeToSellHours) : '—',
    },
    {
      key: 'productCount',
      icon: <Package size={16} strokeWidth={1.75} />,
      label: t('trend.productCount'),
      value: niche.metrics.productCount.toLocaleString(locale),
    },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('trend.detailTitle')}
      width="720px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          {niche.relaunchCriteria != null && (
            <Button variant="primary" icon={<Play size={13} strokeWidth={2} />} onClick={() => onOpenAnalysis(niche)}>
              {t('trend.openAnalysis')}
            </Button>
          )}
        </>
      }
    >
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '6', paddingTop: '2' })}>
        {/* ─── En-tête : image + nom + contexte + croissance ─── */}
        <div className={css({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4' })}>
          <div className={header}>
            {imageUrl ? (
              <img src={imageUrl} alt="" className={headerImg} />
            ) : (
              <span className={headerImgFallback}>
                <ImageOff size={18} strokeWidth={1.5} />
              </span>
            )}
            <div className={css({ minWidth: 0 })}>
              <div className={css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.primary' })}>
                {niche.name}
              </div>
              {context && context.trim().toLowerCase() !== niche.name.trim().toLowerCase() && (
                <div className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{context}</div>
              )}
            </div>
          </div>
          {growth != null && (
            <span className={growthChip({ tier: growth >= 0 ? 'up' : 'down' })} title={growthLabel}>
              {`${growth >= 0 ? '+' : ''}${growth.toLocaleString(locale, { maximumFractionDigits: 0 })}% · ${growthLabel}`}
            </span>
          )}
        </div>

        {/* ─── Stats clés — habillage AnalysisKeyStats ─── */}
        <div className={statsGrid}>
          {stats.map((stat) => (
            <div key={stat.key} className={tile}>
              <span className={tileHeader}>
                {stat.icon}
                {stat.label}
              </span>
              <span className={tileValue}>{stat.value}</span>
            </div>
          ))}
        </div>

        {/* ─── Courbe 14 jours interactive ─── */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
          <span className={sectionTitle}>{t('trend.detailSeries')}</span>
          <DetailCurve series={niche.series} salesLabel={t('statsPanel.seriesSales')} />
        </div>

        {/* ─── Dernières ventes (≤ 5, titre/prix nullables) ─── */}
        {niche.lastSales.length > 0 && (
          <div className={css({ display: 'flex', flexDirection: 'column', gap: '2' })}>
            <span className={sectionTitle}>{t('trend.detailSales')}</span>
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '2' })}>
              {niche.lastSales.map((sale) => (
                <a
                  key={sale.productId}
                  href={sale.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={saleRow}
                >
                  {sale.imageUrl ? (
                    <img src={sale.imageUrl} alt="" className={saleImg} loading="lazy" />
                  ) : (
                    <span className={saleImg} />
                  )}
                  <div className={css({ flex: 1, minWidth: 0 })}>
                    <div
                      className={css({
                        fontSize: 'bodySm',
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {sale.title ?? t('trend.saleUnknownTitle')}
                    </div>
                    {sale.soldAt && (
                      <div className={css({ fontSize: 'micro', color: 'text.tertiary' })}>
                        {new Date(sale.soldAt).toLocaleString(locale, {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                  {sale.price != null && (
                    <span
                      className={css({
                        fontFamily: 'mono',
                        fontSize: 'bodySm',
                        fontWeight: 'semibold',
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {euro(sale.price, 2)}
                    </span>
                  )}
                  <ExternalLink
                    size={13}
                    strokeWidth={1.75}
                    className={css({ color: 'text.tertiary', flexShrink: 0 })}
                  />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
